import { FC, useEffect, useMemo } from 'react';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import { CampaignDetail } from '@/apis/core';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  isRequired,
  isValidInput,
} from '../../validators';

interface SampleMessagesValues {
  sampleMessages: { id: string; message: string }[];
}

interface SampleMessagesFormProps {
  campaign: CampaignDetail;
  onCancel: () => void;
  onSave: (data: {
    sample1?: string | null;
    sample2?: string | null;
    sample3?: string | null;
    sample4?: string | null;
    sample5?: string | null;
  }) => void;
  processing?: boolean;
}

const SampleMessagesForm: FC<SampleMessagesFormProps> = ({
  campaign,
  onCancel,
  onSave,
  processing = false,
}) => {
  const defaultMessages = useMemo(() => {
    const messages = [
      campaign?.sample1,
      campaign?.sample2,
      campaign?.sample3,
      campaign?.sample4,
      campaign?.sample5,
    ].filter((s) => !!s);
    if (messages.length === 0) {
      messages.push('');
    }
    return messages.map((m, i) => ({ id: `${i}`, message: m as string }));
  }, [campaign]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm<SampleMessagesValues>({
    mode: 'onChange',
    defaultValues: {
      sampleMessages: defaultMessages,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sampleMessages',
  });

  useEffect(() => {
    if (campaign) {
      reset({
        sampleMessages: defaultMessages,
      });
    }
  }, [campaign, reset, defaultMessages]);

  const onSubmit = async (values: SampleMessagesValues) => {
    try {
      const messages = values.sampleMessages.map((m) => m.message);
      await onSave({
        sample1: messages[0] || null,
        sample2: messages[1] || null,
        sample3: messages[2] || null,
        sample4: messages[3] || null,
        sample5: messages[4] || null,
      });
    } catch (e) {
      console.error('Failed to save sample messages:', e);
    }
  };

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '4px 2px',
          height: '100%',
          flex: 1,
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              gap: '4px',
              width: '100%',
              lineHeight: 'normal',
            }}
          >
            <Box
              as="h2"
              sx={{
                fontSize: '28px',
                fontWeight: 500,
                color: '#19262A',
                m: 0,
              }}
            >
              Edit Sample Messages
            </Box>
          </Flex>

          <Flex
            sx={{
              flexDirection: 'column',
              gap: '24px',
              width: '100%',
              flex: 1,
              pt: '24px',
              overflowY: 'auto',
            }}
          >
            {fields.map((field, index) => {
              const error = errors.sampleMessages?.[index]?.message;
              return (
                <Flex
                  key={field.id}
                  sx={{
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <Controller
                    name={`sampleMessages.${index}.message`}
                    control={control}
                    rules={{
                      validate: {
                        isRequired,
                        isValidInput,
                        withValidMinLength: createMinLengthValidator(20),
                        withValidMaxLength: createMaxLengthValidator(1024),
                      },
                    }}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <TextInput
                        required={index === 0}
                        multiline
                        error={!!error}
                        note={error?.message}
                        label={`Sample Message ${index + 1}`}
                        maxLength={1024}
                        minRows={3}
                        maxRows={3}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        data-testid={`sampleMessageInput-${index}`}
                      />
                    )}
                  />
                  {index >= defaultMessages.length && (
                    <Button
                      type="button"
                      size="sm"
                      shape="round"
                      sx={{ width: 'fit-content' }}
                      variant="transparent"
                      color="primary"
                      startAdornment={faTrashCan}
                      onClick={() => remove(index)}
                      data-testid={`removeSampleMessageBtn-${index}`}
                    >
                      Remove Message
                    </Button>
                  )}
                </Flex>
              );
            })}
            {fields.length < 5 && (
              <Button
                type="button"
                size="md"
                shape="square"
                variant="transparent"
                color="primary"
                startAdornment={faPlus}
                onClick={() => append({ id: `${Date.now()}`, message: '' })}
                sx={{ width: 'max-content', ml: '4px' }}
                data-testid="addSampleMessageBtn"
              >
                Add Sample Message
              </Button>
            )}
          </Flex>
        </Flex>
        <Flex
          sx={{
            justifyContent: 'flex-end',
            gap: '16px',
            width: '100%',
            pt: '24px',
            mt: 'auto',
          }}
        >
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="primary"
            onClick={onCancel}
            disabled={processing}
            type="button"
            data-testid="cancelSampleMessages"
          >
            Cancel
          </Button>
          <Button
            size="lg"
            shape="square"
            variant="fill"
            color="primary"
            disabled={!isValid || processing}
            type="submit"
            data-testid="saveSampleMessages"
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default SampleMessagesForm;
